"use client";

import "swiper/css/pagination";
import "swiper/css";
import "swiper/css/grid";

import Link from "next/link";
import Image from "next/image";
import dynamic from "next/dynamic";
import { routes } from "@/lib/utils/routes";
import { useAppSelector } from "@/lib/store";
import { no_products_found } from "@/lib/gtm";
import { useTranslation } from "@/i18n/client";
import { getPageType } from "@/lib/utils/functions";
import { logError } from "@/lib/utils/errors/logger";
import { fetchCategoryDetails } from "@/lib/services/category";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import NoRecordFound from "@/assets/image/no-record-found.svg?url";
import GsfMailIcon from "@/components/common/shared/Button/SvgIcons/GsfMailIcon";
import GsfChatIcon from "@/components/common/shared/Button/SvgIcons/GsfChatIcon";

const FallbackListing = dynamic(() =>
  import("./FallbackListing").then((mod) => memo(mod.default)),
);
const Zendesk = dynamic(() => import("@/components/common/zendesk"), {
  ssr: false,
});

const NoListingItem = ({
  heading = "",
  message = "",
  className = "",
  isBrand = false,
  showImage = false,
  showZendesk = true,
  noSearchResult = true,
  showCategories = true,
  parentCategoryId = null,
  currentCategoryId = null,
}) => {
  const { t } = useTranslation(["common"]);
  const { appInitailized } = useAppSelector((state) => state.uiState);

  const categories = useRef([]);
  const [title, setTitle] = useState("");

  const messageText = useMemo(() => {
    if (message) {
      return message;
    } else if (noSearchResult) {
      return t("common:messages.no_results.no_search");
    }
    return false;
  }, [message, noSearchResult]);

  const openZendesk = () => {
    if (window?.zE) {
      window.zE("messenger", "open");
    }
  };

  useEffect(() => {
    if (showCategories && parentCategoryId) {
      const fetchCategoryList = async () => {
        const { data, error } = await fetchCategoryDetails({
          catId: parentCategoryId,
          onlyList: true,
        });

        if (error) {
          logError({
            error: error,
            info: { parentCategoryId },
            message: "fetchCategoryList",
          });
        }

        const parentCategory = data?.categories?.items?.[0] || {};
        const childCategories = parentCategory?.children || [];
        categories.current = childCategories
          .filter((cat) => cat?.id !== currentCategoryId)
          .map((el) => ({
            id: el?.id,
            name: el?.name,
            image: el?.image,
            link: el?.url_path,
          }));

        setTitle(() => {
          return parentCategory?.name
            ? `${parentCategory.name} Categories`
            : "";
        });
      };
      fetchCategoryList();
    }
  }, [parentCategoryId]);

  useEffect(() => {
    if (appInitailized && isBrand) {
      no_products_found({ page_type: getPageType() });
    }
  }, [appInitailized]);

  return (
    <>
      <section className={`register plpNoResult mt-2 ${className}`}>
        {(showCategories && categories.current.length) > 0 && (
          <FallbackListing title={title} categories={categories.current} />
        )}
        <div
          className={`pnf-box w-full ${
            isBrand ? "bg-primary-blue-1300 max-w-full" : "max-w-[870px]"
          }`}
        >
          <div className="flex flex-col items-center border-b border-b-primary-black-100 pb-6 w-full max-w-[1140px]">
            {showImage && (
              <Image
                width={200}
                height={200}
                unoptimized
                src={NoRecordFound}
                alt="No Record Found"
                className="xs:w-[200px] max-w-[114px]"
              />
            )}
            <div>
              <h4 className="lg:text-headline-3 xs:text-headline-4 text-headline-5 xs:mb-4 mb-2 text-primary-black text-center">
                {heading || t("common:messages.no_results.base")}
              </h4>
              {isBrand ? (
                <p className="xs:text-body-text-2 text-body-text-3 text-primary-black-800 text-center xs:mb-8 mb-6">
                  {messageText}
                </p>
              ) : (
                <p className="xs:text-subtitle-2 text-body-text-2-sb text-center xs:mb-8 mb-6">
                  {t("common:messages.no_results.explore_others")}
                </p>
              )}
            </div>

            <div className="flex flex-wrap gap-3 xs:grid xs:gap-4 xs:grid-cols-2 max-w-[260px] mx-auto xs:max-w-full">
              <button
                type="button"
                onClick={openZendesk}
                className="blue-btn-xs font-semibold w-full"
              >
                <GsfChatIcon />
                <span>{t("common:messages.no_results.liveSupport")}</span>
                <span></span>
              </button>
              <Link
                prefetch={false}
                href={routes.contactUs}
                className="font-semibold w-full blue-btn-xs"
              >
                <GsfMailIcon />
                {t("common:messages.no_results.contactUs")}
              </Link>
            </div>
          </div>
          <div className="pt-6 flex flex-col items-center gap-4 w-full">
            <Link
              prefetch={false}
              href={routes.parts}
              className="blue-bordered-btn"
            >
              {t("common:messages.no_results.browseParts")}
            </Link>
          </div>
        </div>
      </section>
      <Zendesk show={showZendesk} />
    </>
  );
};

export default NoListingItem;
